/**
 * ROUND8 §4: the tracker dog's voice (07 §3 lists a bark and a growl). A man's barks are a throat saying nothing
 * in any language; a dog's must never be taken for one. So a woof is shorter and harsher than any man's syllable,
 * starts higher and falls hard, with a rasp of breath and a thump of chest under it: one woof when it gives
 * tongue, a double when it has him at bay, a low rattling growl while it runs his trail toward him, and a yelp
 * when it is hit. Positional like every bark; each dog plays them at its own pitch (`barkRate`).
 */

import type { Layer } from '../synth/patch';
import { PRIORITY, type SfxDef } from './types';

/** Hz a woof starts at; it falls to under half of this in a tenth of a second. */
const WOOF_HZ = 540;

/** One woof at `at` seconds: 0.17 s of throat, rasp and chest. */
export function woof(at: number, from = WOOF_HZ, gain = 1): Layer[] {
  // The throat: a saw that falls hard (sweep < 1 drops fast, then settles), roughened by a fast tremolo.
  const throat = (cutoff: number, q: number, level: number): Layer => ({
    wave: 'saw', at, freq: from, freqEnd: from * 0.42, sweep: 0.55, attack: 0.003, hold: 0.03, decay: 0.13,
    curve: 1.6, gain: gain * level, tremHz: 55, tremDepth: 0.35, filter: { type: 'bp', cutoff, q },
  });
  return [
    throat(850, 2.5, 1),
    throat(1750, 4, 0.55),
    // The rasp: a burst of breath through the open mouth, darkening as it closes.
    {
      wave: 'noise', at, attack: 0.002, hold: 0.015, decay: 0.1, curve: 2, gain: gain * 0.55,
      filter: { type: 'bp', cutoff: 1400, cutoffEnd: 600, q: 1.1 },
    },
    // The chest: a short low thump under the bark.
    { wave: 'sine', at, freq: from * 0.4, freqEnd: from * 0.24, attack: 0.002, decay: 0.09, gain: gain * 0.5 },
  ];
}

/** The growl's rattle, Hz: the throat pulses this fast, which is what makes it a growl and not a hum. */
const GROWL_RATTLE = 27;

function growl(): Layer[] {
  const body = (cutoff: number, q: number, level: number): Layer => ({
    wave: 'saw', freq: 92, freqEnd: 108, sweep: 1.4, vibHz: 3, vibDepth: 0.6, attack: 0.09, hold: 0.45, decay: 0.38,
    curve: 1.3, gain: level, tremHz: GROWL_RATTLE, tremDepth: 0.75, filter: { type: 'bp', cutoff, q },
  });
  return [
    body(420, 2, 1),
    body(900, 3.5, 0.45),
    { wave: 'saw', freq: 92, freqEnd: 108, sweep: 1.4, attack: 0.09, hold: 0.45, decay: 0.38, curve: 1.3, gain: 0.6,
      tremHz: GROWL_RATTLE, tremDepth: 0.75, filter: { type: 'lp', cutoff: 260, q: 1 } },
    {
      wave: 'brown', attack: 0.1, hold: 0.45, decay: 0.35, curve: 1.5, gain: 0.35, tremHz: GROWL_RATTLE, tremDepth: 0.6,
      filter: { type: 'lp', cutoff: 600, q: 0.9 },
    },
  ];
}

/** A yelp: high, thin and falling, then a smaller whimper. Nothing a man makes sounds like it. */
function yelp(): Layer[] {
  const cry = (at: number, from: number, to: number, dur: number, gain: number): Layer[] => [1600, 2700].map(
    (cutoff, i): Layer => ({
      wave: 'saw', at, freq: from, freqEnd: to, sweep: 1.6, attack: 0.004, hold: dur * 0.35, decay: dur * 0.65,
      curve: 1.5, gain: gain * (i === 0 ? 1 : 0.6), filter: { type: 'bp', cutoff, q: 3 },
    }),
  );
  return [...cry(0, 1150, 700, 0.17, 1), ...cry(0.22, 900, 640, 0.14, 0.45)];
}

/** The dog's one-shot sounds: `bark.dogBark` itself lives with the other barks (`BARK_SFX`), built from `woof`. */
export const DOG_SFX = {
  /** At bay: it has him and holds off, giving tongue in pairs. */
  dogBay: {
    patch: {
      layers: [...woof(0), ...woof(0.2, WOOF_HZ * 0.93, 0.85)], jitter: { pitch: 0.04, time: 0.08 }, drive: 1.5,
    },
    variants: 3, priority: PRIORITY.cue, gain: 0.6, maxDistance: 80,
  },
  /** On his trail and closing: low, and only heard near. */
  dogGrowl: {
    patch: { layers: growl(), jitter: { pitch: 0.05, time: 0.1 }, drive: 1.3 },
    variants: 2, priority: PRIORITY.cue, gain: 0.4, maxDistance: 25,
  },
  /** Hit. */
  dogYelp: {
    patch: { layers: yelp(), jitter: { pitch: 0.05, time: 0.06 } },
    variants: 2, priority: PRIORITY.cue, gain: 0.45, maxDistance: 50,
  },
} satisfies Record<string, SfxDef>;

/** One woof, for `BARK_SFX['bark.dogBark']`: the sound it gives tongue with, and mourning its handler. */
export const DOG_BARK: SfxDef = {
  patch: { layers: woof(0), jitter: { pitch: 0.04, time: 0.08 }, drive: 1.5 },
  variants: 3, priority: PRIORITY.cue, gain: 0.55, maxDistance: 80,
};
